import React, { useEffect, useState } from 'react';
import { adminMessagesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/dateUtils';
import { parseApiError } from '../utils/errors';

// Employees: send a message to the super admin and see replies.
// Super admin: see all messages and reply.
export default function AdminMessages() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [items, setItems] = useState([]);
  const [content, setContent] = useState('');
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => adminMessagesAPI.list()
    .then(({ data }) => setItems(data))
    .catch((e) => setError(parseApiError(e, 'Failed to load messages')))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const send = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSaving(true);
    try {
      await adminMessagesAPI.send(content.trim());
      setContent('');
      flash('Message sent to admin');
      load();
    } catch (err) { setError(parseApiError(err, 'Failed to send')); }
    finally { setSaving(false); }
  };

  const submitReply = async (id) => {
    if (!replyText.trim()) return;
    setSaving(true);
    try {
      await adminMessagesAPI.reply(id, replyText.trim());
      setReplyFor(null); setReplyText('');
      flash('Reply sent');
      load();
    } catch (err) { setError(parseApiError(err, 'Failed to reply')); }
    finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div className="page-header"><h2>{isAdmin ? 'Employee Messages' : 'Message Admin'}</h2></div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!isAdmin && (
        <form className="form-card mb-4" onSubmit={send}>
          <div className="form-group">
            <label>Your message to the admin</label>
            <textarea rows={3} value={content} onChange={(e) => setContent(e.target.value)}
              placeholder="Write your message…" required />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>Send to Admin</button>
          </div>
        </form>
      )}

      {loading ? <div className="page-loading">Loading…</div> : items.length === 0 ? (
        <div className="empty-state">No messages yet.</div>
      ) : (
        <div className="concern-list">
          {items.map((m) => (
            <div key={m.id} className="concern-card">
              <div className="concern-header">
                <div>
                  <h4>{isAdmin ? (m.sender?.full_name || 'Employee') : 'To Admin'}</h4>
                  <small className="text-muted">{fmtDate(m.created_at)}</small>
                </div>
                {!m.reply && !m.is_read && <span className="badge badge-orange">New</span>}
                {m.reply && <span className="badge badge-green">Replied</span>}
              </div>
              <p className="concern-content">{m.content}</p>

              {m.reply && (
                <div className="hr-response">
                  <strong>Admin reply{m.replied_at ? ` · ${fmtDate(m.replied_at)}` : ''}:</strong> {m.reply}
                </div>
              )}

              {isAdmin && !m.reply && (
                replyFor === m.id ? (
                  <div style={{ marginTop: 8 }}>
                    <textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write a reply…" style={{ width: '100%' }} />
                    <div className="form-actions">
                      <button className="btn btn-ghost" onClick={() => setReplyFor(null)} disabled={saving}>Cancel</button>
                      <button className="btn btn-primary" onClick={() => submitReply(m.id)} disabled={saving}>Send Reply</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 8 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => { setReplyFor(m.id); setReplyText(''); }}>Reply</button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
